import * as React from 'react';
import { MachineContext } from '../fsm/useMachine';
import TextFlowObject from '../../model/TextFlowObject';
import { fontKV, fontsObject } from '../../model/FontList';
import { useEditorCoordinator } from '../text_editor/editorCoordinator';
import { editWrap, flbArrayAsContent } from '../text_editor/helpers';
import FontArray from '../../model/FontAttrsArray';
import { DELEGATE, EDIT_TEXT } from '../fsm/types';
import { StageBoundsType } from '../root/listeners';

export const FontSelector: React.FC<{size: StageBoundsType}> = ({size}) => {
    const [current, send] = React.useContext(MachineContext)
    let currentObject = current.context.currentObject! as TextFlowObject
    const {globalEditor} = useEditorCoordinator()
    const [globalEditorVal, ] = globalEditor

    const [face, setFace] = React.useState(currentObject.attributedString_flbAttributesArray[0].__typeFace)
    const [style, setStyle] = React.useState(currentObject.attributedString_flbAttributesArray[0].__typeStyle)
    const lastAppliedFont = React.useRef<string>("")

    React.useEffect(() => {
        if (globalEditorVal && globalEditorVal.getMarkAttributes) {
            const val = globalEditorVal.getMarkAttributes("textStyle").fontFamily
            if (val) {
                // Only update if this isn't the font we just applied
                if (val !== lastAppliedFont.current) {
                    const split = val.split("-")
                    setFace(split[0])
                    setStyle(split.length >= 2 ? split[1] : "")
                }
            }
        }
    }, [typeof globalEditorVal, currentObject.attributedString_flbAttributesArray, currentObject.text])

    const changeFontName = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        e.preventDefault()
        e.stopPropagation()

        const newFace = e.currentTarget.value

        // Check if current style is valid for new font, otherwise use first available style
        const availableStyles = fontsObject[newFace] || []
        let newStyle = style

        // If current style isn't valid for new font, use first available style (or empty string)
        if (availableStyles.length > 0 && !availableStyles.includes(style)) {
            newStyle = availableStyles[0]
        }

        const family = createNameFrom(newFace, newStyle)

        if (globalEditorVal) {
            // Load the font before applying it to prevent flash to default font
            try {
                await document.fonts.load(`16px ${family}`)
            } catch (error) {
                console.warn(`[FONT LOAD ERROR] Font ${family} failed to load:`, error)
            }

            if (!globalEditorVal.isEditable) {
                globalEditorVal.commands.setTextSelection({from: 0, to: currentObject.text.length + 1})
            }

            // Update UI state BEFORE applying so dropdown changes immediately
            setFace(newFace)
            setStyle(newStyle)  // Update style too if it changed
            lastAppliedFont.current = family

            editWrap(globalEditorVal, (editor) => {
                editor.chain().focus().setFontFamily(family).run();
            })
        }
    }

    const changeFontStyle = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        e.preventDefault()
        e.stopPropagation()

        const newStyle = e.currentTarget.value
        const family = createNameFrom(face, newStyle)

        if (globalEditorVal) {
            // Load the font before applying it to prevent flash to default font
            try {
                await document.fonts.load(`16px ${family}`)
                console.log(`[FONT STYLE CHANGE] Font ${family} loaded successfully`)
            } catch (error) {
                console.warn(`[FONT STYLE CHANGE] Font ${family} failed to load, applying anyway:`, error)
            }

            if (!globalEditorVal.isEditable) {
                globalEditorVal.commands.setTextSelection({ from: 0, to: currentObject.text.length + 1 })
            }

            // Update UI state BEFORE applying so dropdown changes immediately
            setStyle(newStyle)
            lastAppliedFont.current = family

            editWrap(globalEditorVal, (editor) => {
                editor.chain().focus().setFontFamily(family).run();
            })
        }
    }

    return (
        <React.Fragment>
            <select
                className="location-input"
                title="Font Name"
                onChange={changeFontName}
                id="fontName"
                name="fontName"
                value={face}
            >
                {fontKV.map(kv => kv.key)
                    .sort()
                    .map((key, index) => {

                        return (
                            <option value={key} key={`${index}+${key}`}>
                                {key}
                            </option>
                        )

                    })}
            </select>
            <select
                className="location-input"
                title="Font Style"
                onChange={changeFontStyle}
                id="fontStyle"
                name="fontStyle"
                value={style}
            >
                {fontsObject[face].map((val: string) => {
                    return (
                        <option value={val} key={val}>
                            {val}
                        </option>
                    )
                })}
            </select>

        </React.Fragment>
    )
}

export const createNameFrom = (family: string, style: string) => {
    const hasStyle = fontsObject[family].find((s) => s=== style)
    if (hasStyle) {
        return `${family}-${hasStyle}`
    } else {
        return family
    }
}